import type { ButtonConfig } from "@/components/common/Complete";
import {
    findFirstDueEntryAction,
    type NextDueEntryAction,
} from "@/features/diaries/features/new/services/findFirstDueEntryAction";
import type { FormsToDoItem } from "@/features/diaries/features/new/hooks/useFormsToDo";
import { diaryHomeResultsPath } from "@/features/diaries/routes";
import {
    getPatientFirstSetupStepPath,
    type PatientSetupStepPath,
} from "@/features/diaries/utils/missingDetailsBuilders";
import { formatPatientLongDueDate } from "@/services/time";
import type { SupportedLocale } from "@patient-watch/i18n";
import type { StatefulDiaryT } from "@/types/database/diaries";

import type { JourneyCopy } from "@/features/i18n/journeyCopy";
import { buildSetupTodos, isSetupApplicableForDiary, type JourneyTodos } from "../journeyTodos";

export interface FormCompleteNextScheduledEntry {
    scheduleLabel: string;
    dueAt: string;
}

export type JourneyPhase = "profile" | "consent" | "setup" | "forms" | "done";

export interface NextJourneyPhase {
    phase: JourneyPhase;
    /** User-facing phase label, e.g. "Consent", "Set-up", "Forms". */
    label: string;
    route: Pick<ButtonConfig, "to" | "params" | "search">;
}

export interface JourneyPhaseCompleteScreen {
    title: string;
    description: string;
    statusLabel: string;
    primaryButton: ButtonConfig;
    /** Every due form is finished. The success screen offers charts and logout. */
    allCaughtUp?: boolean;
}

const SETUP_STEP_TO_ROUTE: Record<PatientSetupStepPath, `/diaries/$diaryId/${PatientSetupStepPath}`> = {
    "setup/hospital-number": "/diaries/$diaryId/setup/hospital-number",
    "setup/reference-date": "/diaries/$diaryId/setup/reference-date",
    "setup/side": "/diaries/$diaryId/setup/side",
};

interface GetNextJourneyPhaseInput {
    diary: StatefulDiaryT | null | undefined;
    todos: JourneyTodos;
    diaryId: string;
    setupApplicable?: boolean;
    formsToDo?: FormsToDoItem[];
    locale?: SupportedLocale;
    phaseLabels?: {
        consent: string;
        setup: string;
        forms: string;
        diary: string;
    };
}

function getSetupRoute(diary: StatefulDiaryT, diaryId: string): Pick<ButtonConfig, "to" | "params"> | null {
    const step = getPatientFirstSetupStepPath(diary);
    if (!step) return null;
    return {
        to: SETUP_STEP_TO_ROUTE[step],
        params: { diaryId },
    };
}

function getDiaryHomeRoute(diaryId: string): Pick<ButtonConfig, "to" | "params"> {
    return {
        to: "/diaries/$diaryId/home/to-do",
        params: { diaryId },
    };
}

/** Resolve the next applicable patient journey phase (Profile → Consent → Setup → Forms). */
export function getNextJourneyPhase({
    diary,
    todos,
    diaryId,
    setupApplicable = diary ? isSetupApplicableForDiary(diary) : false,
    formsToDo,
    locale,
    phaseLabels,
}: GetNextJourneyPhaseInput): NextJourneyPhase {
    const labels = {
        consent: phaseLabels?.consent ?? "Consent",
        setup: phaseLabels?.setup ?? "Set-up",
        forms: phaseLabels?.forms ?? "Forms",
        diary: phaseLabels?.diary ?? "Diary",
    };

    const nextConsent = todos.consent.find((item) => !item.done);
    if (nextConsent?.key && todos.consent.length > 0) {
        return {
            phase: "consent",
            label: labels.consent,
            route: {
                to: "/diaries/$diaryId/setup/consent/$consentId",
                params: { diaryId, consentId: nextConsent.key },
            },
        };
    }

    if (diary && setupApplicable && buildSetupTodos(diary).length > 0) {
        const setupRoute = getSetupRoute(diary, diaryId);
        if (setupRoute) {
            return {
                phase: "setup",
                label: labels.setup,
                route: setupRoute,
            };
        }
    }

    const formsOutstanding = todos.forms.some((item) => !item.done);
    if (formsOutstanding && diary) {
        const dueAction = formsToDo && locale ? findFirstDueEntryAction(diaryId, formsToDo, locale) : null;
        if (dueAction && !dueAction.isLocked) {
            return {
                phase: "forms",
                label: labels.forms,
                route: {
                    to: dueAction.to,
                    params: dueAction.params,
                },
            };
        }

        return {
            phase: "forms",
            label: labels.forms,
            route: getDiaryHomeRoute(diaryId),
        };
    }

    return {
        phase: "done",
        label: labels.diary,
        route: getDiaryHomeRoute(diaryId),
    };
}

function getPrimaryButtonText(
    completedPhase: "profile" | "consent" | "setup",
    next: NextJourneyPhase,
    copy: JourneyCopy,
    formButtonLabel?: string
): string {
    if (next.phase === "forms" && formButtonLabel) {
        return formButtonLabel;
    }

    if (completedPhase === "profile") {
        if (next.phase === "consent") return copy.goToConsent;
        if (next.phase === "setup") return copy.goToSetup;
        if (next.phase === "forms") return copy.goToForms;
        return copy.goToYourDiary;
    }

    if (completedPhase === "consent") {
        if (next.phase === "setup") return copy.goToSetup;
        if (next.phase === "forms") return copy.goToForms;
        return copy.goToYourDiary;
    }

    if (next.phase === "consent") return copy.goToConsent;
    if (next.phase === "forms") return copy.goToForms;
    return copy.goToYourDiary;
}

function getCompleteDescription(
    completedPhase: "profile" | "consent" | "setup",
    next: NextJourneyPhase,
    copy: JourneyCopy
): string {
    if (completedPhase === "profile") {
        if (next.phase === "consent") return copy.profileCompleteConsentDescription;
        if (next.phase === "setup") return copy.profileCompleteSetupDescription;
        if (next.phase === "forms") return copy.profileCompleteFormsDescription;
        return copy.profileCompleteDiaryDescription;
    }

    if (completedPhase === "consent") {
        if (next.phase === "setup") return copy.consentCompleteSetupDescription;
        if (next.phase === "forms") return copy.consentCompleteFormsDescription;
        return copy.consentCompleteDiaryDescription;
    }

    if (next.phase === "consent") return copy.setupCompleteConsentDescription;
    if (next.phase === "forms") return copy.setupCompleteFormsDescription;
    return copy.setupCompleteDiaryDescription;
}

function getCompleteTitle(completedPhase: "profile" | "consent" | "setup", copy: JourneyCopy): string {
    if (completedPhase === "profile") return copy.profileCompleteTitle;
    if (completedPhase === "consent") return copy.consentCompleteTitle;
    return copy.setupCompleteTitle;
}

function getCompleteStatusLabel(completedPhase: "profile" | "consent" | "setup", copy: JourneyCopy): string {
    if (completedPhase === "profile") return copy.profileCompleteStatusLabel;
    if (completedPhase === "consent") return copy.consentCompleteStatusLabel;
    return copy.setupCompleteStatusLabel;
}

export function getJourneyPhaseCompleteScreen(
    completedPhase: "profile" | "consent" | "setup",
    next: NextJourneyPhase,
    copy: JourneyCopy,
    options: { formButtonLabel?: string } = {}
): JourneyPhaseCompleteScreen {
    return {
        title: getCompleteTitle(completedPhase, copy),
        description: getCompleteDescription(completedPhase, next, copy),
        statusLabel: getCompleteStatusLabel(completedPhase, copy),
        primaryButton: {
            text: getPrimaryButtonText(completedPhase, next, copy, options.formButtonLabel),
            ...next.route,
        },
    };
}

function getFormCompleteFallbackText(next: NextJourneyPhase, copy: JourneyCopy, hasMoreForms: boolean): string {
    if (hasMoreForms) return copy.continueToNextForm;
    if (next.phase === "consent") return copy.goToConsent;
    if (next.phase === "setup") return copy.goToSetup;
    return copy.goToYourResults;
}

export function getFormJourneyCompleteScreen(
    completedFormLabel: string,
    next: NextJourneyPhase,
    copy: JourneyCopy,
    options: {
        formButtonLabel?: string;
        nextDueEntry?: NextDueEntryAction | null;
        nextScheduledEntry?: FormCompleteNextScheduledEntry | null;
        locale?: SupportedLocale;
    } = {}
): JourneyPhaseCompleteScreen {
    const nextDueRoute =
        options.nextDueEntry && !options.nextDueEntry.isLocked
            ? { to: options.nextDueEntry.to, params: options.nextDueEntry.params }
            : null;
    // A due entry is startable even when the journey phase is still set-up
    // (baseline backup schedules before the reference date is set).
    const hasMoreForms = Boolean(nextDueRoute) || next.phase === "forms";
    const diaryId =
        typeof next.route.params === "object" && next.route.params && "diaryId" in next.route.params
            ? String((next.route.params as { diaryId: string }).diaryId)
            : undefined;

    const allCaughtUp = !hasMoreForms && next.phase === "done";
    const dueDate =
        allCaughtUp && options.nextScheduledEntry
            ? formatPatientLongDueDate(options.nextScheduledEntry.dueAt, options.locale)
            : "";

    let description = hasMoreForms ? copy.formCompleteNextDescription : copy.formCompleteAllDoneDescription;
    if (allCaughtUp) {
        description = dueDate ? copy.formCompleteAllCaughtUpNextDue(dueDate) : "";
    } else if (!hasMoreForms && options.nextScheduledEntry) {
        const scheduledDate = formatPatientLongDueDate(options.nextScheduledEntry.dueAt, options.locale);
        description = `${description} ${copy.formCompleteNextEntryDue(options.nextScheduledEntry.scheduleLabel, scheduledDate)}`;
    }

    const primaryRoute =
        nextDueRoute ??
        (next.phase === "done"
            ? {
                  to: diaryHomeResultsPath,
                  params: diaryId ? { diaryId } : next.route.params,
              }
            : next.route);

    return {
        title: allCaughtUp ? copy.formCompleteAllCaughtUpTitle : copy.formCompleteTitle,
        description,
        statusLabel: allCaughtUp
            ? copy.formCompleteAllCaughtUpTitle
            : `${completedFormLabel} ${copy.formCompleteStatusSuffix}`,
        allCaughtUp,
        primaryButton: {
            text: allCaughtUp
                ? copy.seeCharts
                : (options.formButtonLabel ?? getFormCompleteFallbackText(next, copy, hasMoreForms)),
            ...primaryRoute,
        },
    };
}
